import { createContext, useContext } from 'react';
import { IntlContext, type IntlShape, type MessageDescriptor } from 'react-intl';

const fallbackFormatMessage = (descriptor: MessageDescriptor): string => {
  const { defaultMessage, id } = descriptor;
  if (typeof defaultMessage === 'string') return defaultMessage;
  if (Array.isArray(defaultMessage)) {
    return defaultMessage.map((part) => (typeof part === 'string' ? part : '')).join('');
  }
  return typeof id === 'string' ? id : '';
};

const fallbackIntl = {
  formatMessage: fallbackFormatMessage,
} as unknown as IntlShape;

// Some consumer tests partially mock `react-intl` (e.g. only export `useIntl`),
// which leaves `IntlContext` undefined at module load. Substitute a no-op
// context so `useContext` never throws.
const SafeIntlContext = IntlContext ?? createContext<IntlShape | null>(null);

/**
 * Reads the ambient IntlShape, falling back to a minimal `en`-default intl
 * when no `<IntlProvider>` is present. Design-system primitives use this so
 * consumer unit tests don't break when a DS component adopts react-intl.
 *
 * The fallback only implements `formatMessage` — DS primitives must restrict
 * their react-intl use to `formatMessage` with a `defaultMessage`.
 */
export const useDesignSystemIntl = (): IntlShape =>
  useContext(SafeIntlContext) ?? fallbackIntl;
